import React, { useEffect, useMemo, useState } from 'react'
import { AlertTriangle, Plus } from 'lucide-react'
import { api } from '../../lib/api'
import { clickableRowProps } from '../../lib/a11y'
import { KpiCard, RiskBadge, SessionRail, EmptyState, CredentialingBadge } from '../../components/ui'
import { StatusBanner } from '../../components/StatusBanner'
import { CaseDrawer } from '../../components/CaseDrawer'
import { ProviderDetailDrawer } from '../../components/ProviderDetailDrawer'
import { AddPatientModal } from './AddPatientModal'
import { AddProviderModal } from './AddProviderModal'
import { AddPracticeModal } from './AddPracticeModal'
import { InviteProviderModal } from './InviteProviderModal'
import type {
  ContinuityAlert, ContinuityCase, Patient, Practice, PracticeMetrics, Provider, ProviderInvite,
  OrganizationReadiness, ProviderDiscoverability,
} from '../../lib/types'

const CAPACITY_OPTIONS = ['open', 'limited', 'standby']
const CREDENTIALING_FILTER_OPTIONS = ['all', 'unverified', 'submitted', 'in_review', 'approved', 'rejected']

const ACCOUNT_STATUS_BADGE: Record<string, string> = {
  pending_invite: 'badge-grey',
  invited: 'badge-amber',
  active: 'badge-green',
  suspended: 'badge-red',
}

export default function GroupPracticeDashboard() {
  const [metrics, setMetrics] = useState<PracticeMetrics | null>(null)
  const [alerts, setAlerts] = useState<ContinuityAlert[]>([])
  const [cases, setCases] = useState<ContinuityCase[]>([])
  const [providers, setProviders] = useState<Provider[]>([])
  const [patients, setPatients] = useState<Patient[]>([])
  const [practices, setPractices] = useState<Practice[]>([])
  const [invites, setInvites] = useState<ProviderInvite[]>([])
  const [openCaseId, setOpenCaseId] = useState<string | null>(null)
  const [showAdd, setShowAdd] = useState(false)
  const [showAddProvider, setShowAddProvider] = useState(false)
  const [showAddPractice, setShowAddPractice] = useState(false)
  const [showInviteProvider, setShowInviteProvider] = useState(false)
  const [resendNotice, setResendNotice] = useState<{ url: string; emailSent: boolean } | null>(null)
  const [loading, setLoading] = useState(true)
  const [patientSearch, setPatientSearch] = useState('')
  const [riskFilter, setRiskFilter] = useState<'all' | 'at_risk'>('all')
  const [credentialingFilter, setCredentialingFilter] = useState('all')
  const [openProviderId, setOpenProviderId] = useState<string | null>(null)
  const [orgReadiness, setOrgReadiness] = useState<OrganizationReadiness | null>(null)
  const [discoverabilityByProvider, setDiscoverabilityByProvider] = useState<Record<string, ProviderDiscoverability>>({})

  async function load() {
    setLoading(true)
    const [m, a, c, p, pt, pr, inv, orgR] = await Promise.all([
      api('/v1/practice/metrics'),
      api('/v1/risk-alerts?status=open'),
      api('/v1/cases'),
      api('/providers'),
      api('/patients'),
      api('/practices'),
      api('/provider-invites'),
      api('/v1/practice/organization-readiness'),
    ])
    setMetrics(m); setAlerts(a); setCases(c); setProviders(p); setPatients(pt); setPractices(pr); setInvites(inv); setOrgReadiness(orgR)

    const discoverabilityEntries = await Promise.all(
      p.map((provider: Provider) => api(`/providers/${provider.id}/discoverability`).then((d: ProviderDiscoverability) => [provider.id, d] as const))
    )
    setDiscoverabilityByProvider(Object.fromEntries(discoverabilityEntries))
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  function patientName(id: string) { return patients.find((p) => p.id === id)?.name || 'Patient' }
  function providerName(id: string) { return providers.find((p) => p.id === id)?.name || 'Provider' }

  function latestInviteFor(providerId: string) {
    return invites
      .filter((i) => i.provider_id === providerId && !i.used_at)
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0]
  }

  async function resendInvite(providerId: string) {
    const invite = latestInviteFor(providerId)
    if (!invite) return
    const result = await api(`/provider-invites/${invite.id}/resend`, { method: 'POST' })
    setResendNotice({ url: `${window.location.origin}${result.accept_path}`, emailSent: result.email_sent })
    load()
  }

  const atRiskCaseIds = useMemo(() => new Set(alerts.map((a) => a.case_id)), [alerts])

  const filteredCases = useMemo(() => {
    return cases.filter((c) => {
      if (riskFilter === 'at_risk' && !atRiskCaseIds.has(c.id)) return false
      if (patientSearch.trim()) {
        const q = patientSearch.trim().toLowerCase()
        if (!patientName(c.patient_id).toLowerCase().includes(q) && !providerName(c.provider_id).toLowerCase().includes(q)) return false
      }
      return true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cases, patientSearch, riskFilter, atRiskCaseIds, patients, providers])

  async function updateProviderCapacity(providerId: string, capacityStatus: string) {
    await api(`/providers/${providerId}`, { method: 'PATCH', body: JSON.stringify({ capacity_status: capacityStatus }) })
    load()
  }

  if (loading) return <div className="page-loading">Loading practice dashboard…</div>

  return (
    <div>
      <div className="page-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <h1>Practice continuity queue</h1>
          <div className="ph-sub">Real-time view of cases, risk, and Session 4 progress for your organization.</div>
        </div>
        <button className="btn" onClick={() => setShowAdd(true)}><Plus size={14} style={{ verticalAlign: -2 }} /> Add patient + intake</button>
      </div>

      <div className="kpis">
        <KpiCard label="Active cases" value={metrics?.active_cases ?? 0} />
        <KpiCard label="Patients at risk" value={metrics?.at_risk_count ?? 0} />
        <KpiCard label="Session 4 conversion" value={metrics?.session_4_conversion_rate != null ? `${Math.round(metrics.session_4_conversion_rate * 100)}%` : '—'} />
        <KpiCard label="Total cases" value={metrics?.total_cases ?? 0} />
      </div>

      <div className="section-block">
        <div className="section-head"><h2>Top actions today</h2><span className="sh-note">{alerts.length} need action</span></div>
        {alerts.length === 0 ? (
          <EmptyState icon={<AlertTriangle size={16} />}>No open continuity alerts right now.</EmptyState>
        ) : (
          <div className="queue-list">
            {alerts.map((a) => (
              <div key={a.id} className="queue-item" {...clickableRowProps(() => setOpenCaseId(a.case_id))}>
                <div className="qi-main">
                  <span>{patientName(a.patient_id)} · {providerName(a.provider_id)}</span>
                  <span className="qi-reason">{a.reason}</span>
                </div>
                <RiskBadge band={a.risk_band} />
              </div>
            ))}
          </div>
        )}
      </div>

      {orgReadiness && orgReadiness.readiness_score < 100 && (
        <div className="section-block">
          <div className="section-head"><h2>Organization readiness</h2></div>
          <div className="card">
            <StatusBanner riskLevel="low" journeyLabel={`${orgReadiness.readiness_score}% Ready`} compact />
            <div style={{ marginTop: 10 }}>
              <p className="muted" style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 4 }}>Remaining:</p>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {orgReadiness.remaining.map((r) => <li key={r} style={{ fontSize: 13.5 }}>{r}</li>)}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="section-block">
        <div className="section-head"><h2>Where patients stall on the way to Session 4</h2><span className="sh-note">continuity funnel</span></div>
        <div className="kpis">
          {metrics && Object.entries(metrics.continuity_funnel).map(([k, v]) => (
            <KpiCard key={k} label={k.replace(/_/g, ' ')} value={v} />
          ))}
        </div>
      </div>

      {metrics && metrics.session_4_forecast.approaching_session_4 > 0 && (
        <div className="section-block">
          <div className="section-head"><h2>Session 4 forecast</h2><span className="sh-note">based on your historical rate</span></div>
          <div className="card">
            <p style={{ margin: 0, fontSize: 13.5 }}>
              <b>{metrics.session_4_forecast.approaching_session_4}</b> case{metrics.session_4_forecast.approaching_session_4 === 1 ? ' is' : 's are'} approaching Session 4.
              Based on your historical rate ({metrics.session_4_forecast.historical_conversion_rate_from_session_3 != null ? `${Math.round(metrics.session_4_forecast.historical_conversion_rate_from_session_3 * 100)}%` : '—'}),
              approximately <b>{metrics.session_4_forecast.forecasted_new_retentions ?? '—'}</b> {metrics.session_4_forecast.forecasted_new_retentions === 1 ? 'is' : 'are'} expected to be retained.
            </p>
          </div>
        </div>
      )}

      <div className="section-block">
        <div className="section-head" style={{ justifyContent: 'space-between' }}>
          <h2>Practices</h2>
          <button className="btn small" onClick={() => setShowAddPractice(true)}>+ Add practice</button>
        </div>
        <div className="row-list">
          {practices.map((p) => (
            <div key={p.id} className="row">
              <span>{p.name}{p.location ? ` · ${p.location}` : ''}</span>
              <span className={`badge ${p.status === 'active' ? 'badge-green' : 'badge-grey'}`}>{p.status}</span>
            </div>
          ))}
          {practices.length === 0 && <EmptyState>No practices yet. Practices let you group providers by location or team.</EmptyState>}
        </div>
      </div>

      <div className="section-block">
        <div className="section-head" style={{ justifyContent: 'space-between' }}>
          <h2>Provider roster &amp; retention</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className="sh-note">{providers.length} providers</span>
            <button className="btn small outline" onClick={() => setShowInviteProvider(true)}>Invite provider</button>
            <button className="btn small" onClick={() => setShowAddProvider(true)}>+ Add provider</button>
          </div>
        </div>
        {resendNotice && (
          <div className="card" style={{ marginBottom: 10 }}>
            <p style={{ fontSize: 13.5, margin: 0 }}>
              {resendNotice.emailSent
                ? 'Invite email resent. Or share this link directly:'
                : "The invite email couldn't be sent — copy this link and send it yourself:"}
            </p>
            <input className="inp" readOnly value={resendNotice.url} onFocus={(e) => e.target.select()} style={{ marginTop: 6 }} />
            <button className="btn small outline" style={{ marginTop: 8 }} onClick={() => setResendNotice(null)}>Dismiss</button>
          </div>
        )}
        <div style={{ marginBottom: 10 }}>
          <select className="inp" style={{ maxWidth: 220 }} value={credentialingFilter} onChange={(e) => setCredentialingFilter(e.target.value)}>
            <option value="all">All credentialing statuses</option>
            {CREDENTIALING_FILTER_OPTIONS.filter((s) => s !== 'all').map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
          </select>
        </div>
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th>Provider</th><th>Account</th><th>Credentialing</th><th>Discoverable</th><th>Capacity</th><th>Cases</th><th>Reached Session 4</th><th>Retention</th></tr></thead>
            <tbody>
              {metrics?.provider_retention
                .filter((row) => {
                  if (credentialingFilter === 'all') return true
                  const provider = providers.find((p) => p.id === row.provider_id)
                  return provider?.credentialing_status === credentialingFilter
                })
                .map((row) => {
                const provider = providers.find((p) => p.id === row.provider_id)
                return (
                  <tr key={row.provider_id} {...clickableRowProps(() => provider && setOpenProviderId(provider.id))} style={{ cursor: provider ? 'pointer' : 'default' }}>
                    <td>{row.provider_name}</td>
                    <td>
                      {provider && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }} onClick={(e) => e.stopPropagation()}>
                          <span className={`badge ${ACCOUNT_STATUS_BADGE[provider.account_status] || 'badge-grey'}`}>
                            {provider.account_status.replace('_', ' ')}
                          </span>
                          {provider.account_status === 'invited' && (
                            <button className="btn small outline" style={{ padding: '3px 8px', fontSize: 12 }} onClick={() => resendInvite(provider.id)}>
                              Resend
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                    <td>{provider && <CredentialingBadge status={provider.credentialing_status} />}</td>
                    <td>
                      {provider && discoverabilityByProvider[provider.id] && (
                        <span className={`badge ${discoverabilityByProvider[provider.id].discoverable ? 'badge-green' : 'badge-red'}`}>
                          {discoverabilityByProvider[provider.id].discoverable ? 'Yes' : 'Not Discoverable'}
                        </span>
                      )}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {provider ? (
                        <select
                          className="inp"
                          style={{ padding: '4px 8px', fontSize: 13 }}
                          value={provider.capacity_status}
                          onChange={(e) => updateProviderCapacity(provider.id, e.target.value)}
                        >
                          {CAPACITY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      ) : '—'}
                    </td>
                    <td>{row.total_cases}</td>
                    <td>{row.reached_session_4}</td>
                    <td>{row.retention_rate != null ? `${Math.round(row.retention_rate * 100)}%` : '—'}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="section-block">
        <div className="section-head" style={{ justifyContent: 'space-between' }}>
          <h2>Patients</h2>
          <span className="sh-note">click any patient for detail</span>
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <input
            className="inp"
            style={{ maxWidth: 280 }}
            placeholder="Search by patient or provider name…"
            value={patientSearch}
            onChange={(e) => setPatientSearch(e.target.value)}
          />
          <select className="inp" style={{ maxWidth: 160 }} value={riskFilter} onChange={(e) => setRiskFilter(e.target.value as 'all' | 'at_risk')}>
            <option value="all">All cases</option>
            <option value="at_risk">At risk only</option>
          </select>
        </div>
        <div className="row-list">
          {filteredCases.map((c) => (
            <div key={c.id} className="row" {...clickableRowProps(() => setOpenCaseId(c.id))}>
              <span>{patientName(c.patient_id)} · {providerName(c.provider_id)}</span>
              <SessionRail count={c.session_count} />
            </div>
          ))}
          {filteredCases.length === 0 && (
            <EmptyState>{cases.length === 0 ? 'No cases yet. Add a patient to get started.' : 'No cases match this search/filter.'}</EmptyState>
          )}
        </div>
      </div>

      {openCaseId && (
        <CaseDrawer
          caseId={openCaseId}
          onClose={() => { setOpenCaseId(null); load() }}
          patientName={patientName(cases.find((c) => c.id === openCaseId)?.patient_id || '')}
          providerName={providerName(cases.find((c) => c.id === openCaseId)?.provider_id || '')}
        />
      )}
      {showAdd && <AddPatientModal onClose={() => setShowAdd(false)} onCaseCreated={load} />}
      {showAddProvider && <AddProviderModal practices={practices} onClose={() => setShowAddProvider(false)} onCreated={load} />}
      {showAddPractice && <AddPracticeModal onClose={() => setShowAddPractice(false)} onCreated={load} />}
      {showInviteProvider && <InviteProviderModal providers={providers} onClose={() => setShowInviteProvider(false)} onInvited={load} />}
      {openProviderId && (() => {
        const provider = providers.find((p) => p.id === openProviderId)
        return provider ? (
          <ProviderDetailDrawer provider={provider} onClose={() => { setOpenProviderId(null); load() }} onUpdated={load} />
        ) : null
      })()}
    </div>
  )
}
